import { z } from 'zod';

import { consentSourceSchema, consentStatusSchema } from './enums.js';
import { paginationQuerySchema } from './pagination.js';
import { phoneE164Schema } from './phone.js';

/** Sources a human or the ERP may assert. `inbound_stop` is written by the webhook only. */
export const assertableConsentSourceSchema = consentSourceSchema.exclude(['inbound_stop']);

export const createRecipientSchema = z
  .object({
    phone: phoneE164Schema,
    name: z.string().trim().max(200).optional(),
    externalRef: z.string().trim().max(200).optional(),
    consent: z.literal('opted_in'),
    consentSource: assertableConsentSourceSchema,
    consentedAt: z.string().datetime().optional(),
    metadata: z.record(z.unknown()).default({}),
  })
  .strict();
export type CreateRecipientInput = z.input<typeof createRecipientSchema>;

export const updateRecipientSchema = z
  .object({
    name: z.string().trim().max(200).nullable().optional(),
    externalRef: z.string().trim().max(200).nullable().optional(),
    metadata: z.record(z.unknown()).optional(),
  })
  .strict();

export const consentChangeSchema = z
  .object({
    source: assertableConsentSourceSchema,
    note: z.string().trim().max(1000).optional(),
  })
  .strict();

/** Opting out is destructive to a customer relationship — a note is mandatory. */
export const optOutSchema = consentChangeSchema.extend({
  note: z.string().trim().min(1, 'A reason is required').max(1000),
});

export const consentEventDtoSchema = z.object({
  id: z.string(),
  action: consentStatusSchema,
  source: consentSourceSchema,
  actor: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export const recipientDtoSchema = z.object({
  id: z.string(),
  phone: z.string(),
  name: z.string().nullable(),
  externalRef: z.string().nullable(),
  consent: consentStatusSchema,
  consentSource: z.string(),
  consentedAt: z.string().datetime(),
  optedOutAt: z.string().datetime().nullable(),
  metadata: z.record(z.unknown()),
  lastMessageAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type RecipientDto = z.infer<typeof recipientDtoSchema>;

export const listRecipientsQuerySchema = paginationQuerySchema.extend({
  consent: consentStatusSchema.optional(),
  q: z.string().trim().max(200).optional(),
});

export const importRecipientsSchema = z
  .object({
    consentSource: assertableConsentSourceSchema,
    /** Raw CSV text: header `phone,name,externalRef,consentedAt`. */
    csv: z.string().min(1),
  })
  .strict();

export const importRowResultSchema = z.object({
  row: z.number().int(),
  phone: z.string(),
  status: z.enum(['created', 'updated', 'skipped', 'error']),
  message: z.string().optional(),
});
export const importResultSchema = z.object({
  total: z.number().int(),
  created: z.number().int(),
  updated: z.number().int(),
  skipped: z.number().int(),
  errors: z.number().int(),
  results: z.array(importRowResultSchema),
});
export type ImportResult = z.infer<typeof importResultSchema>;
